/**
 * idb.ts — Lightweight IndexedDB wrapper
 *
 * Provides a clean promise-based API over raw IndexedDB.
 * Used by Zustand persist middleware to store cart, catalog, and offline queue.
 *
 * Features:
 * - Simple open/get/put/delete/clear/getAll operations
 * - Graceful fallback when IndexedDB is unavailable (private browsing, etc.)
 * - Version-managed upgrades (new object stores added automatically)
 * - No external dependencies
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DB_NAME = 'kapi-pos';
const DB_VERSION = 1;

/** Object stores created in the DB */
export const IDB_STORES = {
  /** POS cart state (pos-store) */
  POS_CART: 'pos-cart',
  /** Product catalog cache (catalog-store) */
  CATALOG: 'catalog',
  /** Auth / session info (auth-store) */
  AUTH: 'auth',
  /** Offline sync queue (sync-store) */
  SYNC_QUEUE: 'sync-queue',
} as const;

export type IDBStoreName = (typeof IDB_STORES)[keyof typeof IDB_STORES];

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let dbPromise: Promise<IDBDatabase> | null = null;
let dbUnavailable = false;

// ---------------------------------------------------------------------------
// Open / upgrade
// ---------------------------------------------------------------------------

/**
 * Open (or reuse) the KapiPOS IndexedDB database.
 * Returns null if IndexedDB is not available in this environment.
 */
export async function openDB(): Promise<IDBDatabase | null> {
  if (dbUnavailable) return null;
  if (!('indexedDB' in window)) {
    dbUnavailable = true;
    return null;
  }

  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create all stores if they don't exist
        for (const storeName of Object.values(IDB_STORES)) {
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName);
          }
        }
      };

      request.onsuccess = (event) => {
        resolve((event.target as IDBOpenDBRequest).result);
      };

      request.onerror = (event) => {
        dbUnavailable = true;
        dbPromise = null;
        reject((event.target as IDBOpenDBRequest).error);
      };

      request.onblocked = () => {
        console.warn('[idb] Database upgrade blocked — close other tabs');
      };
    });

    dbPromise.catch(() => {
      dbPromise = null;
    });
  }

  try {
    return await dbPromise;
  } catch (err) {
    console.warn('[idb] IndexedDB unavailable:', err);
    dbUnavailable = true;
    return null;
  }
}

// ---------------------------------------------------------------------------
// CRUD helpers
// ---------------------------------------------------------------------------

/**
 * Get a value by key from an object store.
 * Returns undefined if not found or if IndexedDB is unavailable.
 */
export async function idbGet<T>(store: IDBStoreName, key: IDBValidKey): Promise<T | undefined> {
  const db = await openDB();
  if (!db) return undefined;

  return new Promise<T | undefined>((resolve, reject) => {
    try {
      const tx = db.transaction(store, 'readonly');
      const req = tx.objectStore(store).get(key);
      req.onsuccess = () => resolve(req.result as T | undefined);
      req.onerror = () => reject(req.error);
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Put (insert or update) a value by key.
 * No-ops silently if IndexedDB is unavailable.
 */
export async function idbPut<T>(store: IDBStoreName, key: IDBValidKey, value: T): Promise<void> {
  const db = await openDB();
  if (!db) return;

  return new Promise<void>((resolve, reject) => {
    try {
      const tx = db.transaction(store, 'readwrite');
      const req = tx.objectStore(store).put(value, key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Delete a key from an object store.
 */
export async function idbDelete(store: IDBStoreName, key: IDBValidKey): Promise<void> {
  const db = await openDB();
  if (!db) return;

  return new Promise<void>((resolve, reject) => {
    try {
      const tx = db.transaction(store, 'readwrite');
      const req = tx.objectStore(store).delete(key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Get all values from an object store as an array.
 */
export async function idbGetAll<T>(store: IDBStoreName): Promise<T[]> {
  const db = await openDB();
  if (!db) return [];

  return new Promise<T[]>((resolve, reject) => {
    try {
      const tx = db.transaction(store, 'readonly');
      const req = tx.objectStore(store).getAll();
      req.onsuccess = () => resolve(req.result as T[]);
      req.onerror = () => reject(req.error);
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Get all keys from an object store.
 */
export async function idbGetAllKeys(store: IDBStoreName): Promise<IDBValidKey[]> {
  const db = await openDB();
  if (!db) return [];

  return new Promise<IDBValidKey[]>((resolve, reject) => {
    try {
      const tx = db.transaction(store, 'readonly');
      const req = tx.objectStore(store).getAllKeys();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Clear all records from an object store.
 */
export async function idbClear(store: IDBStoreName): Promise<void> {
  const db = await openDB();
  if (!db) return;

  return new Promise<void>((resolve, reject) => {
    try {
      const tx = db.transaction(store, 'readwrite');
      const req = tx.objectStore(store).clear();
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    } catch (err) {
      reject(err);
    }
  });
}

// ---------------------------------------------------------------------------
// Availability check
// ---------------------------------------------------------------------------

/**
 * Returns true if IndexedDB is available in the current browser context.
 * Useful for showing offline capability warnings.
 */
export function isIDBAvailable(): boolean {
  return !dbUnavailable && 'indexedDB' in window;
}

/**
 * Reset internal state (for tests only).
 * @internal
 */
export function _resetIDBState(): void {
  dbPromise = null;
  dbUnavailable = false;
}

/** La cola de dinero no admite el fallback silencioso de los caches. */
export async function updateSyncQueue<T>(change: (rows: Map<string, T>) => void): Promise<T[]> {
  const db = await openDB();
  if (!db) throw new Error('IndexedDB no disponible. La venta no se guardó; conserva el carrito.');
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORES.SYNC_QUEUE, 'readwrite', { durability: 'strict' });
    const store = tx.objectStore(IDB_STORES.SYNC_QUEUE);
    const rows = new Map<string, T>();
    const original = new Map<string, string>();
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        rows.set(String(cursor.key), cursor.value as T);
        original.set(String(cursor.key), JSON.stringify(cursor.value));
        cursor.continue();
        return;
      }
      try {
        change(rows);
        for (const key of original.keys()) if (!rows.has(key)) store.delete(key);
        for (const [key, value] of rows) if (original.get(key) !== JSON.stringify(value)) store.put(value, key);
      } catch (err) { tx.abort(); reject(err); }
    };
    // El éxito de put no garantiza COMMIT: cuota/disco pueden abortar la transacción después.
    tx.oncomplete = () => resolve([...rows.values()]);
    tx.onabort = () => reject(tx.error ?? new Error('Se abortó el guardado de la venta offline'));
    tx.onerror = () => reject(tx.error ?? new Error('No se pudo guardar la venta offline'));
  });
}

/** El intento online también debe sobrevivir a una respuesta perdida tras COMMIT. */
export async function idbPutCommitted<T>(store: IDBStoreName, key: IDBValidKey, value: T): Promise<void> {
  const db = await openDB();
  if (!db) throw new Error('IndexedDB no disponible. Conserva el carrito; no se envió otra venta.');
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite', { durability: 'strict' });
    tx.objectStore(store).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('Se abortó el guardado del intento de venta'));
    tx.onerror = () => reject(tx.error ?? new Error('No se pudo guardar el intento de venta'));
  });
}
